import {Injectable} from '@angular/core';
import {PlanModelService} from './plan-model.service';
import {AppConfigService} from './app-config.service';
import { HttpClient } from '@angular/common/http';
import {AttachmentValidationService} from './attachment-validation.service';
import {ATTACHMENT_CSD, LAND_CSV, PDF_CREATION_METHOD} from '../constants';
import {AlertModalService} from '../component/modal';
import {PlanLodgementService} from './plan-lodgement.service';
import {first} from 'rxjs';
import {Fee} from '../models/fee.model';
import {NgEventBus} from 'ng-event-bus';
import {SpatialUtilService} from '../../design/services/spatial-util.service';


@Injectable({
    providedIn: 'root'
})
export class FeeService {
    title = 'Fee Service';

    previousLotCount: number = 0;

    constructor(
        private eventBus: NgEventBus,
        private http: HttpClient,
        private appConfigService: AppConfigService,
        private alertModalService: AlertModalService,
        private attachmentValidationService: AttachmentValidationService,
        private planModelService: PlanModelService,
        private planLodgementService: PlanLodgementService,
        private spatialUtilService: SpatialUtilService
    ) {}

    calculateChargeablePlanLots() {
        const planModel = this.planModelService.getModel();
        if (planModel.pdfCreationMethod === PDF_CREATION_METHOD.DIGITAL_PLAN) {
            // planModel.chargeablePlanLots calculated and updated in DigitalPlanComponent#ngDoCheck
            return;
        }

        if (
            planModel.lodgementType.value.toUpperCase() === 'AMENDMENT' &&
            !this.isPDFCreationMethodSurveyorPortal() &&
            !this.attachmentValidationService.hasCSDorCSVAttachment()
        ) {
            return;
        }

        if (!planModel.planPurpose || planModel.planPurpose.maxNumberLotsCreated === 0) {
            planModel.chargeablePlanLots = 0;
            return;
        }

        if (this.isPDFCreationMethodSurveyorPortal()) {
            planModel.chargeablePlanLots = this.spatialUtilService.getChargeableLots().length;
        } else if (planModel.attachments) {
            const attachment = planModel.attachments.find(
                a => [ATTACHMENT_CSD, LAND_CSV].includes(a.attachmentType) && a.optionality === 'Mandatory'
            );

            const isAttachmentOptional =
                planModel.attachments.find(a => a.attachmentType === ATTACHMENT_CSD && a.optionality === 'Optional') &&
                !planModel.attachments.find(a => a.attachmentType === LAND_CSV);

            if (attachment && !attachment.fileName) {
                planModel.chargeablePlanLots = 0;
            } else if (attachment && attachment.fileName && this.attachmentValidationService.hasValidationInfo(attachment.attachmentType)) {
                const validationErrors = this.attachmentValidationService.getErrors(attachment.attachmentType);
                const totalChargeableLotCount = this.attachmentValidationService.getTotalChargeableLotCount(attachment.attachmentType);
                if (
                    validationErrors.some(
                        v =>
                            v.includes('the document must be in version 2.0 format') ||
                            v.includes('Invalid Lot Record CSV file, parsing error') ||
                            v.includes('lot numbers must be unique')
                    )
                ) {
                    if (validationErrors.some(v => v.includes('the document must be in version 2.0 format'))) {
                        planModel.chargeablePlanLots = 0;
                    } else if (
                        validationErrors.some(
                            v => v.includes('Invalid Lot Record CSV file, parsing error') || v.includes('lot numbers must be unique')
                        )
                    ) {
                        planModel.chargeablePlanLots = 0;
                    }
                } else if (totalChargeableLotCount === null) {
                    planModel.chargeablePlanLots = 0;
                } else if (typeof totalChargeableLotCount === 'number') {
                    planModel.chargeablePlanLots = totalChargeableLotCount + (planModel.numberOfInsetLots || 0);
                }
            } else if (isAttachmentOptional) {
                if (planModel.formerTenures[0]) {
                    planModel.chargeablePlanLots = planModel.formerTenures[0].lots.length;
                } else {
                    planModel.chargeablePlanLots = 0;
                }
            } else {
                planModel.chargeablePlanLots = 0;
            }
        } else {
            planModel.chargeablePlanLots = 0;
        }
    }

    populateFees() {
        const logPrefix = `${this.title} - populateFees`;
        console.debug(logPrefix);

        const planModel = this.planModelService.getModel();

        if (!planModel.tenureType || !planModel.planType || !planModel.planPurpose) {
            planModel.fees = [];
            this.alertModalService.createAlertMessage(
                'The plan lodgement fees could not be calculated. The tenure type, plan type and plan purpose fields must be completed on the ' +
                    'information screen before the fees can be calculated.'
            );
            return;
        }

        const url = this.appConfigService.settings.apiGatewayUrl + '/finance/fees/calculate';
        console.log("ChargeablePlanLots: " + planModel.chargeablePlanLots);
        const requestData = {
            numberOfLots: planModel.chargeablePlanLots,
            tenureType: planModel.tenureType.name,
            planType: planModel.planType.name,
            lodgementType: planModel.lodgementType.value,
            planPurpose: planModel.planPurpose.name,
            dolInstructedPlan: planModel.crownStateLand?.dolInstructedPlan
        };

        const calculateFeesSubscription = this.http
            .post(url, requestData)
            .pipe(first())
            .subscribe({
                next: (fees: Fee[]) => {
                    console.debug(`${logPrefix} - success calculating fees.`);

                    if (!planModel.fees) {
                        planModel.fees = [];
                    }

                    planModel.fees = fees.map(
                        v =>
                            planModel.fees.find(
                                f =>
                                    f.description === v.description &&
                                    f.quantity === v.quantity &&
                                    f.itemCode === v.itemCode &&
                                    f.baseAmount === v.baseAmount &&
                                    f.total === v.total &&
                                    f.totalTax === v.totalTax
                            ) || v
                    );
                },
                error: error => {
                    console.error(`${logPrefix} - error calculating fees:`, error);
                    this.alertModalService.createAlertMessage(
                        'There is an issue identifying fees information relevant to the plan to be lodged. Please contact Landgate Customer ' +
                            'Services. Lodgement will not be possible until this issue is resolved. Landgate apologises for any inconvenience caused.'
                    );
                }
            });

        this.eventBus.cast('nlr:busy', calculateFeesSubscription);

        this.previousLotCount = 0;

        if (planModel.versionNumber > 1) {
            const getPreviousLotCountSubscription = this.planLodgementService
                .getPreviousVersionNumberLotCount(planModel.id)
                .pipe(first())
                .subscribe(lotCount => (this.previousLotCount = lotCount['data']));

            this.eventBus.cast('nlr:busy', getPreviousLotCountSubscription);
        }
    }

    private isPDFCreationMethodSurveyorPortal() {
        return this.planModelService.getModel().pdfCreationMethod === PDF_CREATION_METHOD.SURVEYOR_PORTAL;
    }
}
